/**
 * Lead Data Fetcher
 * Fetches lead data from external API for form pre-fill
 */

export interface ExternalLeadData {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  streetAddress: string
  city: string
  state: string
  zipCode: string
  dob: string // MM/DD/YYYY
  ssn: string // XXX-XX-XXXX
  requestedLoanAmount: string
  housingStatus: string
  housingPayment: string
  employmentStatus: string
  grossAnnualIncome: string
  creditAuth: boolean
  tcpA: boolean
  smsAuth: boolean
  refId: string
  // Additional fields from API (not used for form pre-fill)
  id?: number
  source?: string
  [key: string]: any
}

/**
 * Fetch lead data from external API by refId
 * @param refId - The reference ID from URL parameter
 * @param apiUrl - Base API URL (default: https://dcapi.maceinnovation.com)
 * @param authKey - API authentication key
 * @returns ExternalLeadData or null if fetch fails
 */
export async function fetchLeadByRefId(
  refId: string,
  apiUrl: string = 'https://dcapi.maceinnovation.com',
  authKey: string = ''
): Promise<ExternalLeadData | null> {
  if (!refId || refId.trim().length === 0) {
    console.error('❌ fetchLeadByRefId: refId is required')
    return null
  }

  if (!authKey || authKey.trim().length === 0) {
    console.error('❌ fetchLeadByRefId: API auth key is required')
    return null
  }

  const url = `${apiUrl}/api/leads/byexternaltransactionid/${encodeURIComponent(refId)}`
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

  try {
    console.log(`🔍 Fetching lead data for refId: ${refId}`)
    console.log(`   URL: ${url}`)
    console.log(`   Auth key length: ${authKey.length}`)
    console.log(`   Auth key preview: ${authKey.substring(0, 10)}...`)

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-auth': authKey
      },
      signal: controller.signal
    })

    console.log(`   Response status: ${response.status} ${response.statusText}`)

    clearTimeout(timeoutId)

    if (!response.ok) {
      console.error(`❌ API request failed: HTTP ${response.status} ${response.statusText}`)
      console.error(`   URL: ${url}`)
      return null
    }

    const data: ExternalLeadData = await response.json()

    // Validate required fields
    if (!data.firstName || !data.lastName || !data.email) {
      console.error('❌ API response missing required fields (firstName, lastName, email)')
      console.error('   Received data:', data)
      return null
    }

    console.log(`✅ Lead data fetched successfully for refId: ${refId}`)
    console.log(`   Name: ${data.firstName} ${data.lastName}`)
    console.log(`   Email: ${data.email}`)

    return data

  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.error('❌ API request timed out after 10 seconds')
      } else {
        console.error('❌ Error fetching lead data:', error.message)
      }
    } else {
      console.error('❌ Unknown error fetching lead data:', error)
    }

    return null
  }
}

/**
 * Validate external lead data structure
 * @param data - Data to validate
 * @returns true if data is valid
 */
export function validateLeadData(data: any): data is ExternalLeadData {
  if (!data || typeof data !== 'object') {
    return false
  }

  // Check required string fields
  const requiredFields = ['firstName', 'lastName', 'email', 'refId']
  for (const field of requiredFields) {
    if (typeof data[field] !== 'string' || data[field].trim().length === 0) {
      console.warn(`⚠️ Invalid or missing required field: ${field}`)
      return false
    }
  }

  return true
}
